
import React, { useEffect } from "react";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { User } from "lucide-react";
import { Link } from "react-router-dom";

interface SimplifiedDashboardProps {
  firstName?: string;
  error?: string;
}

export const SimplifiedDashboard: React.FC<SimplifiedDashboardProps> = ({ firstName, error }) => {
  useEffect(() => {
    console.log('[SimplifiedDashboard] Mounting with:', { firstName, error });
    
    // Force visibility on mount with inline styles
    const container = document.querySelector('[data-testid="simplified-dashboard"]');
    if (container instanceof HTMLElement) {
      container.style.cssText = `
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
        position: relative !important;
        z-index: 1 !important;
        background-color: #ffffff !important;
        min-height: 100vh !important;
      `;
    }

    // Also force body visibility
    document.body.style.cssText = `
      display: block !important;
      visibility: visible !important;
      opacity: 1 !important;
      background-color: #ffffff !important;
    `;
  }, [firstName, error]);

  return (
    <div 
      className="page-container scrollable-container pt-safe" 
      data-testid="simplified-dashboard"
      style={{
        display: 'block',
        visibility: 'visible',
        opacity: 1,
        backgroundColor: '#ffffff',
        minHeight: '100vh',
        position: 'relative',
        zIndex: 1
      }}
    >
      <div className="content-wrapper">
        {/* Header with forced styles */}
        <header 
          className="flex items-center justify-between p-4 bg-transparent" 
          data-testid="simplified-dashboard-header"
          style={{
            display: 'flex',
            visibility: 'visible',
            opacity: 1,
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '1rem',
            backgroundColor: 'transparent'
          }}
        >
          <div style={{ flexGrow: 1 }}>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: '0 0 0.5rem 0' }}>
              Hello {firstName || "there"}!
            </h1>
            <p style={{ color: '#6b7280', margin: 0 }}>Welcome to LiveTrack.</p>
          </div>
          <div style={{ marginLeft: '1rem', display: 'flex', alignItems: 'center' }}>
            <Link to="/account/profile">
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: '#e5e7eb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <User style={{ height: '24px', width: '24px', color: '#6b7280' }} />
              </div>
            </Link>
          </div>
        </header>

        <main 
          className="content-container space-y-6 mx-0 px-4 pb-24 w-full"
          style={{
            display: 'block',
            visibility: 'visible',
            opacity: 1,
            padding: '0 1rem 6rem 1rem'
          }}
        >
          {error && (
            <Card style={{ borderColor: '#fb923c', backgroundColor: '#fef3c7' }}>
              <CardContent style={{ paddingTop: '1.5rem' }}>
                <p style={{ color: '#b45309', fontSize: '0.875rem', margin: '0 0 0.5rem 0' }}>
                  Your dashboard is running in simplified mode.
                </p>
                <p style={{ color: '#d97706', fontSize: '0.75rem', margin: 0 }}>
                  Status: {error}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Animals Card */}
          <Card>
            <CardHeader>
              <h2 style={{ fontSize: '1.125rem', fontWeight: '600', margin: 0 }}>Your Animals</h2>
            </CardHeader>
            <CardContent>
              <div style={{ textAlign: 'center', padding: '1.5rem 0' }}>
                <p style={{ color: '#6b7280', margin: '0 0 0.5rem 0' }}>
                  Animal data will load once account access is restored.
                </p>
                <Link 
                  to="/animals" 
                  style={{ 
                    color: '#2563eb', 
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    display: 'inline-block'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.color = '#1d4ed8'}
                  onMouseOut={(e) => e.currentTarget.style.color = '#2563eb'}
                >
                  View Animals →
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Activity Card */}
          <Card>
            <CardHeader>
              <h2 style={{ fontSize: '1.125rem', fontWeight: '600', margin: 0 }}>Recent Activity</h2>
            </CardHeader>
            <CardContent>
              <div style={{ textAlign: 'center', padding: '1.5rem 0' }}>
                <p style={{ color: '#6b7280', margin: '0 0 0.5rem 0' }}>
                  Activity will load once account access is restored.
                </p>
                <Link 
                  to="/animals" 
                  style={{ 
                    color: '#2563eb', 
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    display: 'inline-block'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.color = '#1d4ed8'}
                  onMouseOut={(e) => e.currentTarget.style.color = '#2563eb'}
                >
                  Add New Event →
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <h2 style={{ fontSize: '1.125rem', fontWeight: '600', margin: 0 }}>Quick Actions</h2>
            </CardHeader>
            <CardContent>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(2, 1fr)', 
                gap: '0.75rem' 
              }}>
                <Link 
                  to="/animals/new" 
                  style={{
                    padding: '0.75rem',
                    backgroundColor: '#dbeafe',
                    borderRadius: '0.5rem',
                    textAlign: 'center',
                    textDecoration: 'none',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#bfdbfe'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#dbeafe'}
                >
                  <p style={{ color: '#1d4ed8', fontWeight: '500', margin: 0 }}>Add Animal</p>
                </Link>
                <Link 
                  to="/events/new" 
                  style={{
                    padding: '0.75rem',
                    backgroundColor: '#dcfce7',
                    borderRadius: '0.5rem',
                    textAlign: 'center',
                    textDecoration: 'none',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#bbf7d0'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#dcfce7'}
                >
                  <p style={{ color: '#166534', fontWeight: '500', margin: 0 }}>Log Event</p>
                </Link>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    </div>
  );
};
